import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Environment Configuration - MUST BE FIRST
import { validateEnvironmentConfig, config } from './config/environment';
import { api, authApi } from './api/client';

// Validate configuration on app load
validateEnvironmentConfig();

// Import Shadcn UI components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';

// Authentication
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import InvitationAcceptance from './components/InvitationAcceptance';
import RequirePermission from './components/RequirePermission';

// Version Badge
import VersionBadge from './components/VersionBadge';

// Legal Pages
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsOfUse from './components/legal/TermsOfUse';
import CookiePolicy from './components/legal/CookiePolicy';
import LegalDisclosure from './components/legal/LegalDisclosure';
import DoNotSell from './components/legal/DoNotSell';

// Hooks
import { usePermission } from './hooks/usePermission';

// Theme Provider
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { ThemeToggle } from './components/ThemeToggle';

// Material-UI Theme Provider
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Enterprise Components
import EnterpriseHeader from './components/EnterpriseHeader';
import EnterpriseNavigation from './components/EnterpriseNavigation';

// Icons
import { Plus, Package, Package2, DollarSign, TrendingUp, Calculator, Search, Edit, Eye, Upload, Download, Settings, BarChart3, FileText, List, Brain, Bell } from 'lucide-react';

// Import ProductMaster component
import ProductMaster from './components/ProductMaster';
import InventoryDashboard from './components/InventoryDashboard';
import InventoryLayout from './pages/Inventory/InventoryLayout';
import ProductMasterDetail from './components/ProductMasterDetail';
import InsightsDashboard from './components/InsightsDashboard';
import ProductCreateWizard from './components/ProductCreateWizard';
import ExecutiveDashboard from './components/ExecutiveDashboard';
import DashboardHub from './pages/Dashboard/DashboardHub';
import SalesHub from './pages/Sales/SalesHub';
import InventoryHub from './pages/Inventory/InventoryHub';
import FinanceHub from './pages/Finance/FinanceHub';
import GoToMarketStrategy from './pages/Sales/GoToMarketStrategy';

// Phase 3: Full-Page Product Detail Routing
import ProductDetailPage from './components/ProductDetailPage';

// Phase 4.3: Workflow Automation - Notifications
import { NotificationProvider, useNotifications } from './contexts/NotificationContext';
import NotificationPanel from './components/NotificationPanel';

// Phase 1: Smart Pricing Calculator
import SmartPricingCalculator from './components/SmartPricingCalculator';

// Purchase Orders
import { PurchaseOrdersManager } from './pages/PurchaseOrders';
// import PurchaseOrderDetail from './pages/PurchaseOrders/PurchaseOrderDetail';

// Finance Module
import Finance from './pages/Finance';

// Sales Module
import Sales from './pages/Sales';

// Settings Module
import SettingsPage from './components/Settings/SettingsPage';
import ProfileSettings from './components/Settings/ProfileSettings';
import Preferences from './components/Settings/Preferences';

// Use centralized configuration
const BACKEND_URL = config.backendUrl;
const API = config.apiBaseUrl;

// Material-UI Theme Creator
const createAppTheme = (mode) => createTheme({
  palette: {
    mode,
    ...(mode === 'dark' ? {
      background: {
        default: '#1a1a1a',
        paper: '#2a2a2a',
      },
      text: {
        primary: '#ffffff',
        secondary: '#b0b0b0',
      },
    } : {
      background: {
        default: '#ffffff',
        paper: '#f5f5f5',
      },
      text: {
        primary: '#000000',
        secondary: '#666666',
      },
    }),
  },
});

// Notification Bell Component
const NotificationBell = () => {
  const { unreadCount } = useNotifications();
  const [showNotifications, setShowNotifications] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowNotifications(!showNotifications)}
        className="relative gap-2"
      >
        <Bell className="w-4 h-4" />
        Alerts
        {unreadCount > 0 && (
          <Badge 
            variant="destructive" 
            className="absolute -top-2 -right-2 min-w-[1.25rem] h-5 text-xs flex items-center justify-center"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </Button>
      
      <NotificationPanel 
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />
    </>
  );
};

// Inner App component that uses theme
function AppContent() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Get theme from custom ThemeContext
  const { theme } = useTheme();
  
  // Get authenticated user's tenant_id
  const { tenantId } = useAuth();
  
  // Create Material-UI theme based on custom theme
  const muiTheme = React.useMemo(() => createAppTheme(theme), [theme]);
  
  const handleTabChange = (value) => {
    setActiveTab(value);
  };
  const [products, setProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [attributeTemplates, setAttributeTemplates] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [zenMode, setZenMode] = useState(false);

  // Load data when tenantId becomes available
  useEffect(() => {
    if (tenantId) {
      initializePetYupp();
    }
  }, [tenantId]);

  const initializePetYupp = async () => {
    if (!tenantId) return; // Wait for tenant_id
    try {
      setLoading(true);
      // Setup PetYupp categories and templates
      await api.post('/api/v1/setup/petyupp');
      
      // Load initial data
      await Promise.all([
        fetchDashboard(),
        fetchSubscription(),
        fetchProducts(),
        fetchVariants(),
        fetchCategories(),
        fetchAttributeTemplates()
      ]);
      
      toast.success('PetYupp ZuperBooks initialized successfully!');
    } catch (error) {
      console.error('Error initializing PetYupp:', error);
      toast.error('Failed to initialize PetYupp setup');
    } finally {
      setLoading(false);
    }
  };

  // Fetch functions
  const fetchDashboard = async () => {
    if (!tenantId) return; // Wait for tenant_id to be available
    try {
      const data = await api.get(`/api/v1/dashboard/summary?tenant_id=${tenantId}`);
      setDashboardData(data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard data');
    }
  };

  const fetchSubscription = async () => {
    if (!tenantId) {
      console.warn('⚠️ fetchSubscription called but tenantId is not set');
      return;
    }
    
    console.log('🚀 Starting subscription fetch for tenant:', tenantId);
    
    try {
      // Use centralized API client with built-in retry logic
      const subscriptionData = await authApi.getSubscription(tenantId);
      
      console.log('✅ Subscription data received:', subscriptionData);
      setSubscriptionData(subscriptionData);
      console.log('✅ Subscription state updated successfully');
      return;
      
    } catch (error) {
      console.error('❌ FINAL ERROR - All subscription fetch attempts failed');
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      // 🎯 CRITICAL: Use default free subscription - NEVER block the app
      // This ensures the application continues to function even if:
      // - Backend is down
      // - Subscription API fails
      // - Network issues occur
      // The app degrades gracefully with free tier features
      console.log('🔄 Using default free plan to keep app functional');
      
      setSubscriptionData({
        subscription: {
          plan_id: 'plan-starter',
          status: 'ACTIVE',
          limits_json: {
            max_skus: 100,
            max_channels: 2,
            api_calls_per_min: 20,
            pricing_suggestions_per_hour: 50
          },
          features_json: {
            pricing_engine: true,
            shopify_push: false,
            ads_attribution: false,
            api_integration: false
          }
        },
        plan: {
          id: 'plan-starter',
          name: 'Starter (Fallback)',
          plan_type: 'STARTER',
          monthly_price: 29.0
        },
        entitlements: {
          pricing_engine: true,
          shopify_push: false,
          ads_attribution: false,
          api_integration: false
        },
        limits: {
          max_skus: 100,
          max_channels: 2,
          api_calls_per_min: 20,
          pricing_suggestions_per_hour: 50
        }
      });
      
      // Optional: Show a non-intrusive notification to user
      // toast.info('Using offline mode - some features may be limited');
    }
  };

  const fetchProducts = async () => {
    if (!tenantId) return; // Wait for tenant_id to be available
    try {
      const response = await api.get(`/api/v1/products?tenant_id=${tenantId}&search=${searchTerm}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    }
  };

  const fetchVariants = async () => {
    if (!tenantId) return; // Wait for tenant_id to be available
    try {
      const response = await api.get(`/api/v1/variants?tenant_id=${tenantId}`);
      setVariants(response.data);
    } catch (error) {
      console.error('Error fetching variants:', error);
      toast.error('Failed to load variants');
    }
  };

  const fetchCategories = async () => {
    if (!tenantId) return; // Wait for tenant_id to be available
    try {
      const [mainCats, subCats] = await Promise.all([
        api.get(`/api/v1/categories?tenant_id=${tenantId}`),
        api.get(`/api/v1/categories?tenant_id=${tenantId}&parent_id=cat-bowls-feeders`)
      ]);
      setCategories(mainCats.data);
      setSubcategories(subCats.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const fetchAttributeTemplates = async () => {
    if (!tenantId) return; // Wait for tenant_id to be available
    try {
      const response = await api.get(`/api/v1/attribute-templates?tenant_id=${tenantId}`);
      setAttributeTemplates(response.data);
    } catch (error) {
      console.error('Error fetching attribute templates:', error);
      toast.error('Failed to load attribute templates');
    }
  };

  useEffect(() => {
    if (activeTab === 'products') {
      fetchProducts();
    }
  }, [searchTerm]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-700">Initializing PetYupp ZuperBooks...</h2>
          <p className="text-slate-500 mt-2">Setting up your product catalog and costing system</p>
        </div>
      </div>
    );
  }

  return (
    <MuiThemeProvider theme={muiTheme}>
      <NotificationProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes - Login/Landing Page */}
            <Route path="/" element={<Login />} />
            <Route path="/login" element={<Login />} />
            
            {/* Public Route - Invitation Acceptance */}
            <Route path="/invite/:token" element={<InvitationAcceptance />} />
            
            {/* Public Routes - Legal Pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-use" element={<TermsOfUse />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/legal-disclosure" element={<LegalDisclosure />} />
            <Route path="/do-not-sell" element={<DoNotSell />} />
            
            {/* Protected Routes - All App Content */}
            <Route path="/*" element={
              <ProtectedRoute>
                <div className="App min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
                  <div className="max-w-none w-full px-4 md:px-6">
                    {/* Enterprise Header */}
                    <EnterpriseHeader 
                      subscriptionData={subscriptionData}
                      notificationCount={3}
                    />

                    {/* Enterprise Navigation */}
                    <EnterpriseNavigation 
                      activeTab={activeTab}
                      onTabChange={handleTabChange}
                    />

                    {/* Main Content */}
                    <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                      <div className="mt-2">

                      {/* Dashboard Tab */}
                      <TabsContent value="dashboard" className="px-0">
                        <DashboardHub />
                      </TabsContent>

                      {/* Inventory Tab - Now Inventory Hub */}
                      <TabsContent value="inventory" className="px-0">
                        <RequirePermission module="inventory">
                          <InventoryHub />
                        </RequirePermission>
                      </TabsContent>

                      {/* Product Master Tab - Redirects to Inventory Hub */}
                      <TabsContent value="product-master" className="px-0">
                        <RequirePermission module="products">
                          <InventoryHub />
                        </RequirePermission>
                      </TabsContent>

                      {/* Purchase Orders Tab */}
                      <TabsContent value="purchase-orders" className="px-0">
                        <RequirePermission module="purchase_orders">
                          <PurchaseOrdersManager />
                        </RequirePermission>
                      </TabsContent>

                      {/* Finance Module Tab - Now Finance Hub */}
                      <TabsContent value="finance" className="px-0">
                        <RequirePermission module="finance">
                          <FinanceHub />
                        </RequirePermission>
                      </TabsContent>

                      {/* Smart Pricing Calculator Tab */}
                      <TabsContent value="pricing" className="px-0">
                        <RequirePermission module="pricing">
                          <SmartPricingCalculator />
                        </RequirePermission>
                      </TabsContent>

                      {/* Sales Tab - Sales Hub with Orders, Settlements, Analysis, Forecast */}
                      <TabsContent value="sales" className="px-0">
                        <RequirePermission module="sales">
                          <SalesHub />
                        </RequirePermission>
                      </TabsContent>

                      {/* Insights Tab */}
                      <TabsContent value="insights" className="px-0">
                        <RequirePermission module="insights">
                          <InsightsDashboard />
                        </RequirePermission>
                      </TabsContent>

                      {/* Reports Tab */}
                      <TabsContent value="reports" className="px-0">
                        <RequirePermission module="reports">
                          <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md border border-gray-200 dark:border-gray-700">
                            <div className="text-center py-12">
                              <FileText className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                              <h3 className="text-lg font-medium text-gray-600 dark:text-gray-300 mb-2">Reports & Analytics</h3>
                              <p className="text-gray-500 dark:text-gray-400 mb-6">Query-based reporting for accounting, performance, and financial summaries</p>
                              <div className="text-sm text-gray-400 dark:text-gray-500">Planned: Future Release</div>
                            </div>
                          </div>
                        </RequirePermission>
                      </TabsContent>

                      {/* Settings Tab */}
                      <TabsContent value="settings" className="px-0">
                        <RequirePermission module="settings">
                          <SettingsPage />
                        </RequirePermission>
                      </TabsContent>

                      </div>
                    </Tabs>
                    
                    <Toaster position="top-right" />
                  </div>
                </div>
              </ProtectedRoute>
            } />
            
            {/* Profile Settings Route */}
            <Route path="/profile" element={
              <ProtectedRoute>
                <div className="min-h-screen bg-white dark:bg-neutral-900">
                  <EnterpriseHeader />
                  <div className="container mx-auto px-4 py-6">
                    <ProfileSettings />
                  </div>
                </div>
              </ProtectedRoute>
            } />
            
            {/* Preferences Route */}
            <Route path="/preferences" element={
              <ProtectedRoute>
                <div className="min-h-screen bg-white dark:bg-neutral-900">
                  <EnterpriseHeader />
                  <div className="container mx-auto px-4 py-6">
                    <Preferences />
                  </div>
                </div>
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
      </NotificationProvider>
  </MuiThemeProvider>
  );
}

// Main App wrapper with ThemeProvider
function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <AppContent />
        <VersionBadge />
      </ThemeProvider>
    </AuthProvider>
  );
}

// Enhanced Dashboard Component
function Dashboard({ data, loading, onRefresh, categories }) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <Card key={i} className="animate-pulse border-0 shadow-xl bg-white/80 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg mb-3"></div>
              <div className="h-8 bg-gradient-to-r from-gray-300 to-gray-400 rounded-lg"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="shadow-md bg-white" style={{border: '1px solid #BFBFBF'}}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium mb-2" style={{color: '#666666'}}>Total Products</p>
                <p className="text-3xl font-bold" style={{color: '#333333'}}>{data?.total_products || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#427CAC'}}>
                <Package className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md bg-white" style={{border: '1px solid #BFBFBF'}}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium mb-2" style={{color: '#666666'}}>Total SKUs</p>
                <p className="text-3xl font-bold" style={{color: '#333333'}}>{data?.total_skus || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#2B7D2B'}}>
                <Edit className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md bg-white" style={{border: '1px solid #BFBFBF'}}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium mb-2" style={{color: '#666666'}}>Avg Landed Cost</p>
                <p className="text-3xl font-bold" style={{color: '#333333'}}>${data?.avg_landed_cost?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#AB218E'}}>
                <DollarSign className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md bg-white" style={{border: '1px solid #BFBFBF'}}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium mb-2" style={{color: '#666666'}}>High-Cost SKUs</p>
                <p className="text-3xl font-bold" style={{color: '#333333'}}>{data?.high_cost_skus?.length || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#C14646'}}>
                <Calculator className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Categories Breakdown */}
      <Card className="shadow-md bg-white" style={{border: '1px solid #BFBFBF'}}>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-3 text-xl font-bold" style={{color: '#333333'}}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{backgroundColor: '#427CAC'}}>
              <BarChart3 className="w-4 h-4 text-white" />
            </div>
            Categories Breakdown
          </CardTitle>
          <CardDescription className="font-medium" style={{color: '#666666'}}>Product distribution by category</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data?.categories_breakdown && Object.entries(data.categories_breakdown).map(([category, count]) => (
              <div key={category} className="flex justify-between items-center p-4 rounded-lg hover:shadow-sm transition-all duration-200" style={{backgroundColor: '#EFF4F9', border: '1px solid #E5E5E5'}}>
                <span className="font-medium" style={{color: '#333333'}}>{category}</span>
                <Badge className="text-white border-0 font-medium px-3 py-1" style={{backgroundColor: '#427CAC'}}>{count} products</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Cost Updates and High Cost SKUs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-md bg-white" style={{border: '1px solid #BFBFBF'}}>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3 text-xl font-bold" style={{color: '#333333'}}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{backgroundColor: '#2B7D2B'}}>
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
              Recent Cost Updates
            </CardTitle>
            <CardDescription className="font-medium" style={{color: '#666666'}}>Latest SKU cost changes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.recent_cost_updates?.length > 0 ? (
                data.recent_cost_updates.map((sku, index) => (
                  <div key={index} className="flex justify-between items-start p-4 rounded-lg hover:shadow-sm transition-all duration-200" style={{backgroundColor: '#EFF4F9', border: '1px solid #E5E5E5'}}>
                    <div className="flex-1">
                      <p className="font-semibold text-sm" style={{color: '#333333'}}>{sku.sku}</p>
                      <p className="text-xs mt-1" style={{color: '#666666'}}>{sku.product_name}</p>
                      <p className="text-xs" style={{color: '#666666'}}>{sku.category}{sku.subcategory && ` > ${sku.subcategory}`}</p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="font-bold text-lg" style={{color: '#2B7D2B'}}>${sku.landed_cost.toFixed(2)}</p>
                      <p className="text-xs" style={{color: '#666666'}}>
                        {new Date(sku.last_updated).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">No cost updates yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* High Cost SKUs */}
        <Card className="shadow-md bg-white" style={{border: '1px solid #BFBFBF'}}>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3 text-xl font-bold" style={{color: '#333333'}}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{backgroundColor: '#C14646'}}>
                <Calculator className="w-4 h-4 text-white" />
              </div>
              High-Cost SKUs
            </CardTitle>
            <CardDescription className="font-medium" style={{color: '#666666'}}>SKUs with landed cost greater than $50</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.high_cost_skus?.length > 0 ? (
                data.high_cost_skus.map((sku, index) => (
                  <div key={index} className="flex justify-between items-start p-4 rounded-lg hover:shadow-sm transition-all duration-200" style={{backgroundColor: '#EFF4F9', border: '1px solid #E5E5E5'}}>
                    <div className="flex-1">
                      <p className="font-semibold text-sm" style={{color: '#333333'}}>{sku.sku}</p>
                      <p className="text-xs mt-1" style={{color: '#666666'}}>{sku.product_name}</p>
                      <p className="text-xs" style={{color: '#666666'}}>{sku.category}{sku.subcategory && ` > ${sku.subcategory}`}</p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="font-bold text-lg" style={{color: '#C14646'}}>${sku.landed_cost.toFixed(2)}</p>
                      <Badge className="text-white border-0 text-xs mt-2 px-2 py-1" style={{backgroundColor: '#C14646'}}>High Cost</Badge>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">No high-cost SKUs</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Refresh Button */}
      <div className="flex justify-center">
        <Button 
          onClick={onRefresh} 
          className="text-white border-0 shadow-md hover:shadow-lg transition-all duration-200 gap-3 px-8 py-3 rounded-lg"
          style={{backgroundColor: '#427CAC'}}
        >
          <TrendingUp className="w-5 h-5" />
          Refresh Dashboard
        </Button>
      </div>
    </div>
  );
}

// Enhanced Products Tab Component
function ProductsTab({ products, categories, subcategories, attributeTemplates, searchTerm, onSearchChange, onRefresh }) {
  return (
    <div className="space-y-6">
      {/* Search & Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <CreateProductDialog 
            categories={categories}
            subcategories={subcategories}
            attributeTemplates={attributeTemplates}
            onSuccess={onRefresh} 
          />
          <ProductCreateWizard onSuccess={onRefresh} />
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product) => (
          <ProductCard 
            key={product.id} 
            product={product} 
            categories={categories}
            subcategories={subcategories}
          />
        ))}
        
        {products.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="p-12 text-center">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">No products found</h3>
              <p className="text-slate-500 mb-6">Start by creating your first ZuperBooks product</p>
              <div className="flex gap-4 justify-center">
                <CreateProductDialog 
                  categories={categories}
                  subcategories={subcategories}
                  attributeTemplates={attributeTemplates}
                  onSuccess={onRefresh} 
                />
                <ProductCreateWizard onSuccess={onRefresh} />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// Enhanced Product Card Component
function ProductCard({ product, categories, subcategories }) {
  const category = categories.find(c => c.id === product.category_id);
  const subcategory = subcategories.find(sc => sc.id === product.subcategory_id);
  
  return (
    <Card className="hover:shadow-2xl transition-all duration-300 border-0 shadow-lg bg-white/90 backdrop-blur-sm hover:bg-white/95 transform hover:-translate-y-1">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-bold text-gray-800">{product.name}</CardTitle>
        <CardDescription>
          <span className="font-mono text-xs bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 px-3 py-1 rounded-lg font-medium">
            {product.sku_root}
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {product.brand && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 font-medium">Brand:</span>
              <span className="text-sm font-semibold text-gray-800">{product.brand}</span>
            </div>
          )}
          {category && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 font-medium">Category:</span>
              <span className="text-sm font-semibold text-gray-800">{category.name}</span>
            </div>
          )}
          {subcategory && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 font-medium">Subcategory:</span>
              <span className="text-sm font-semibold text-gray-800">{subcategory.name}</span>
            </div>
          )}
          <Badge 
            className={`mt-3 shadow-lg ${
              product.status === 'ACTIVE' 
                ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white' 
                : 'bg-gradient-to-r from-gray-400 to-gray-500 text-white'
            }`}
          >
            {product.status}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}

// Create Product Dialog Component
function CreateProductDialog({ categories, subcategories, attributeTemplates, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    sku_root: '',
    name: '',
    brand: 'PetYupp',
    category_id: 'cat-bowls-feeders',
    subcategory_id: '',
    description: '',
    attribute_template_id: 'template-bowls-feeders'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.post('/api/v1/products', {
        ...formData,
        tenant_id: tenantId
      });
      
      toast.success('Product created successfully!');
      setOpen(false);
      setFormData({ 
        sku_root: '', name: '', brand: 'PetYupp', category_id: 'cat-bowls-feeders',
        subcategory_id: '', description: '', attribute_template_id: 'template-bowls-feeders'
      });
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          data-testid="create-product-btn" 
          className="bg-blue-600 hover:bg-blue-700 text-white border-2 border-blue-600 hover:border-blue-700 shadow-md hover:shadow-lg transition-all duration-200 gap-2 font-medium"
        >
          <Plus className="w-4 h-4" />
          Create Product
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create New ZuperBooks Product</DialogTitle>
          <DialogDescription>
            Add a new product to your Bowls & Feeders catalog
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="sku_root">SKU Root *</Label>
            <Input
              id="sku_root"
              data-testid="product-sku-input"
              value={formData.sku_root}
              onChange={(e) => setFormData(prev => ({ ...prev, sku_root: e.target.value }))}
              placeholder="e.g., FBUCKET"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="name">Product Name *</Label>
            <Input
              id="name"
              data-testid="product-name-input"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Stainless Steel Flat Bucket"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="brand">Brand</Label>
              <Input
                id="brand"
                value={formData.brand}
                onChange={(e) => setFormData(prev => ({ ...prev, brand: e.target.value }))}
                placeholder="PetYupp"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="subcategory_id">Subcategory</Label>
              <Select 
                value={formData.subcategory_id} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, subcategory_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select subcategory" />
                </SelectTrigger>
                <SelectContent>
                  {subcategories.map(subcat => (
                    <SelectItem key={subcat.id} value={subcat.id}>
                      {subcat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Product description..."
              rows={3}
            />
          </div>
          
          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? 'Creating...' : 'Create Product'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Variants Tab Component (reuse existing but enhanced)
function VariantsTab({ variants, products, categories, subcategories, onRefresh }) {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Product Variants</h2>
        <CreateVariantDialog products={products} onSuccess={onRefresh} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {variants.map((variant) => (
          <VariantCard key={variant.id} variant={variant} products={products} />
        ))}
        
        {variants.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="p-12 text-center">
              <Edit className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">No variants found</h3>
              <p className="text-slate-500 mb-6">Create your first product variant</p>
              <CreateVariantDialog products={products} onSuccess={onRefresh} />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// Variant Card Component
function VariantCard({ variant, products }) {
  const product = products.find(p => p.id === variant.product_id);
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{product?.name || 'Unknown Product'}</CardTitle>
        <CardDescription>
          <span className="font-mono text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
            {variant.sku}
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {variant.barcode && (
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Barcode:</span>
              <span className="text-sm font-mono">{variant.barcode}</span>
            </div>
          )}
          
          <div className="flex justify-between">
            <span className="text-sm text-slate-600">Unit of Measure:</span>
            <Badge variant="outline">{variant.unit_of_measure}</Badge>
          </div>
          
          {variant.attributes && Object.keys(variant.attributes).length > 0 && (
            <div>
              <span className="text-sm text-slate-600 block mb-1">Attributes:</span>
              <div className="flex flex-wrap gap-1">
                {Object.entries(variant.attributes).map(([key, value]) => (
                  <Badge key={key} variant="outline" className="text-xs">
                    {key}: {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          <div className="pt-2">
            <CostVersionDialog variant={variant} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Create Variant Dialog (Enhanced)
function CreateVariantDialog({ products, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    product_id: '',
    sku: '',
    barcode: '',
    unit_of_measure: 'PCS',
    attributes: {},
    weight_grams: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        tenant_id: tenantId,
        weight_grams: formData.weight_grams ? parseFloat(formData.weight_grams) : null
      };
      
      await api.post('/api/v1/variants', payload);
      
      toast.success('Variant created successfully!');
      setOpen(false);
      setFormData({ 
        product_id: '', sku: '', barcode: '', unit_of_measure: 'PCS',
        attributes: {}, weight_grams: ''
      });
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create variant');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-variant-btn" className="gap-2">
          <Plus className="w-4 h-4" />
          Create Variant
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Variant</DialogTitle>
          <DialogDescription>
            Add a new variant to an existing product
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="product_id">Product *</Label>
            <Select value={formData.product_id} onValueChange={(value) => setFormData(prev => ({ ...prev, product_id: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select a product" />
              </SelectTrigger>
              <SelectContent>
                {products.map(product => (
                  <SelectItem key={product.id} value={product.id}>
                    {product.name} ({product.sku_root})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="sku">SKU *</Label>
            <Input
              id="sku"
              data-testid="variant-sku-input"
              value={formData.sku}
              onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
              placeholder="e.g., FBUCKET-S-SS"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="barcode">Barcode</Label>
              <Input
                id="barcode"
                value={formData.barcode}
                onChange={(e) => setFormData(prev => ({ ...prev, barcode: e.target.value }))}
                placeholder="Barcode"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="unit_of_measure">Unit of Measure</Label>
              <Select 
                value={formData.unit_of_measure} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, unit_of_measure: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PCS">PCS (Pieces)</SelectItem>
                  <SelectItem value="SET">SET (Set)</SelectItem>
                  <SelectItem value="QT">QT (Quart)</SelectItem>
                  <SelectItem value="PT">PT (Pint)</SelectItem>
                  <SelectItem value="IN">IN (Inch)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? 'Creating...' : 'Create Variant'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Cost Version Dialog Component (reuse existing)
function CostVersionDialog({ variant }) {
  const [open, setOpen] = useState(false);
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    effective_from: new Date().toISOString().split('T')[0],
    purchase_cost: '',
    freight: '0',
    duty: '0',
    packaging: '0',
    handling: '0',
    overhead_alloc: '0',
    notes: ''
  });
  
  const fetchCosts = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/v1/variants/${variant.id}/costs`);
      setCosts(response.data);
    } catch (error) {
      console.error('Error fetching costs:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (open) {
      fetchCosts();
    }
  }, [open]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        tenant_id: tenantId,
        purchase_cost: parseFloat(formData.purchase_cost),
        freight: parseFloat(formData.freight),
        duty: parseFloat(formData.duty),
        packaging: parseFloat(formData.packaging),
        handling: parseFloat(formData.handling),
        overhead_alloc: parseFloat(formData.overhead_alloc)
      };
      
      await api.post(`/api/v1/variants/${variant.id}/costs`, payload);
      
      toast.success('Cost version created successfully!');
      setFormData({
        effective_from: new Date().toISOString().split('T')[0],
        purchase_cost: '',
        freight: '0',
        duty: '0',
        packaging: '0',
        handling: '0',
        overhead_alloc: '0',
        notes: ''
      });
      fetchCosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create cost version');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="manage-costs-btn" className="w-full gap-2">
          <DollarSign className="w-4 h-4" />
          Manage Costs
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cost Versions - {variant.sku}</DialogTitle>
          <DialogDescription>
            Manage effective-dated cost components for this variant
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Add New Cost Version */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Add New Cost Version</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="effective_from">Effective From *</Label>
                    <Input
                      id="effective_from"
                      type="date"
                      value={formData.effective_from}
                      onChange={(e) => setFormData(prev => ({ ...prev, effective_from: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="purchase_cost">Purchase Cost *</Label>
                    <Input
                      id="purchase_cost"
                      type="number"
                      step="0.01"
                      value={formData.purchase_cost}
                      onChange={(e) => setFormData(prev => ({ ...prev, purchase_cost: e.target.value }))}
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="freight">Freight</Label>
                    <Input
                      id="freight"
                      type="number"
                      step="0.01"
                      value={formData.freight}
                      onChange={(e) => setFormData(prev => ({ ...prev, freight: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="duty">Duty</Label>
                    <Input
                      id="duty"
                      type="number"
                      step="0.01"
                      value={formData.duty}
                      onChange={(e) => setFormData(prev => ({ ...prev, duty: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="packaging">Packaging</Label>
                    <Input
                      id="packaging"
                      type="number"
                      step="0.01"
                      value={formData.packaging}
                      onChange={(e) => setFormData(prev => ({ ...prev, packaging: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="handling">Handling</Label>
                    <Input
                      id="handling"
                      type="number"
                      step="0.01"
                      value={formData.handling}
                      onChange={(e) => setFormData(prev => ({ ...prev, handling: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="overhead_alloc">Overhead Allocation</Label>
                    <Input
                      id="overhead_alloc"
                      type="number"
                      step="0.01"
                      value={formData.overhead_alloc}
                      onChange={(e) => setFormData(prev => ({ ...prev, overhead_alloc: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Optional notes about this cost version..."
                    rows={2}
                  />
                </div>
                
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? 'Adding...' : 'Add Cost Version'}
                </Button>
              </form>
            </CardContent>
          </Card>
          
          {/* Existing Cost Versions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Cost History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {costs.length > 0 ? (
                  costs.map((cost, index) => (
                    <div key={cost.id} className="p-4 border rounded-lg bg-slate-50">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-slate-600">Effective From</p>
                          <p className="font-medium">{cost.effective_from}</p>
                        </div>
                        <div>
                          <p className="text-slate-600">Purchase Cost</p>
                          <p className="font-medium">${cost.purchase_cost.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-slate-600">Total Landed Cost</p>
                          <p className="font-bold text-green-600">
                            ${(cost.purchase_cost + cost.freight + cost.duty + cost.packaging + cost.handling + cost.overhead_alloc).toFixed(2)}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-slate-500">COGS Breakdown:</p>
                          <p className="text-xs">Freight: ${cost.freight.toFixed(2)}</p>
                          <p className="text-xs">Duty: ${cost.duty.toFixed(2)}</p>
                          <p className="text-xs">Pack: ${cost.packaging.toFixed(2)}</p>
                          <p className="text-xs">Handle: ${cost.handling.toFixed(2)}</p>
                          <p className="text-xs">Overhead: ${cost.overhead_alloc.toFixed(2)}</p>
                        </div>
                      </div>
                      {cost.notes && (
                        <p className="text-xs text-slate-600 mt-2 italic">{cost.notes}</p>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500 text-center py-8">No cost versions yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Pricing Tab Component (reuse existing)
function PricingTab({ variants }) {
  const [selectedVariant, setSelectedVariant] = useState('');
  const [pricingRequest, setPricingRequest] = useState({
    channel: 'SHOPIFY',
    target_margin_pct: 0.35,
    estimated_fees_pct: 0.029,
    min_price: '',
    max_price: ''
  });
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const handleCalculate = async () => {
    if (!selectedVariant) {
      toast.error('Please select a variant');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        variant_id: selectedVariant,
        ...pricingRequest,
        min_price: pricingRequest.min_price ? parseFloat(pricingRequest.min_price) : null,
        max_price: pricingRequest.max_price ? parseFloat(pricingRequest.max_price) : null
      };
      
      const response = await api.post('/api/v1/pricing/suggest', payload);
      setSuggestion(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate pricing');
    } finally {
      setLoading(false);
    }
  };
  
  const selectedVariantData = variants.find(v => v.id === selectedVariant);
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Pricing Calculator</h2>
        <p className="text-slate-600">Calculate optimal selling prices based on your costs and target margins</p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Form */}
        <Card>
          <CardHeader>
            <CardTitle>Pricing Parameters</CardTitle>
            <CardDescription>Configure your pricing calculation</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="variant_select">Select Variant *</Label>
              <Select value={selectedVariant} onValueChange={setSelectedVariant}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a variant" />
                </SelectTrigger>
                <SelectContent>
                  {variants.map(variant => (
                    <SelectItem key={variant.id} value={variant.id}>
                      {variant.sku}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="channel">Channel</Label>
              <Select 
                value={pricingRequest.channel} 
                onValueChange={(value) => setPricingRequest(prev => ({ ...prev, channel: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SHOPIFY">Shopify (2.9%)</SelectItem>
                  <SelectItem value="AMAZON">Amazon (8-15%)</SelectItem>
                  <SelectItem value="WALMART">Walmart (6-8%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="target_margin">Target Margin %</Label>
                <Input
                  id="target_margin"
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={pricingRequest.target_margin_pct}
                  onChange={(e) => setPricingRequest(prev => ({ ...prev, target_margin_pct: parseFloat(e.target.value) }))}
                  placeholder="0.35"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="fees_pct">Platform Fees %</Label>
                <Input
                  id="fees_pct"
                  type="number"
                  step="0.001"
                  min="0"
                  max="1"
                  value={pricingRequest.estimated_fees_pct}
                  onChange={(e) => setPricingRequest(prev => ({ ...prev, estimated_fees_pct: parseFloat(e.target.value) }))}
                  placeholder="0.029"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="min_price">Min Price</Label>
                <Input
                  id="min_price"
                  type="number"
                  step="0.01"
                  value={pricingRequest.min_price}
                  onChange={(e) => setPricingRequest(prev => ({ ...prev, min_price: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="max_price">Max Price</Label>
                <Input
                  id="max_price"
                  type="number"
                  step="0.01"
                  value={pricingRequest.max_price}
                  onChange={(e) => setPricingRequest(prev => ({ ...prev, max_price: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
            </div>
            
            <Button 
              onClick={handleCalculate} 
              disabled={loading || !selectedVariant}
              data-testid="calculate-price-btn"
              className="w-full gap-2"
            >
              <Calculator className="w-4 h-4" />
              {loading ? 'Calculating...' : 'Calculate Price'}
            </Button>
          </CardContent>
        </Card>
        
        {/* Results */}
        <Card>
          <CardHeader>
            <CardTitle>Pricing Suggestion</CardTitle>
            <CardDescription>
              {selectedVariantData ? `For ${selectedVariantData.sku}` : 'Select a variant to see results'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {suggestion ? (
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    ${suggestion.suggested_price}
                  </div>
                  <p className="text-slate-600">Suggested Selling Price</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-blue-600 font-medium">Landed Cost</p>
                    <p className="text-lg font-bold">${(Number(suggestion.landed_cost) || 0).toFixed(2)}</p>
                  </div>
                  
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-green-600 font-medium">Actual Margin</p>
                    <p className="text-lg font-bold">{((Number(suggestion.estimated_margin_pct) || 0) * 100).toFixed(1)}%</p>
                  </div>
                  
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <p className="text-purple-600 font-medium">Target Margin</p>
                    <p className="text-lg font-bold">{((Number(suggestion.target_margin_pct) || 0) * 100).toFixed(1)}%</p>
                  </div>
                  
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <p className="text-orange-600 font-medium">Platform Fees</p>
                    <p className="text-lg font-bold">{((Number(suggestion.estimated_fees_pct) || 0) * 100).toFixed(1)}%</p>
                  </div>
                </div>
                
                {suggestion.notes && suggestion.notes.length > 0 && (
                  <div className="p-3 bg-yellow-50 rounded-lg">
                    <p className="text-yellow-800 font-medium mb-2">Notes:</p>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      {suggestion.notes.map((note, index) => (
                        <li key={index}>• {note}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calculator className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Configure parameters and click Calculate to see pricing suggestions</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Bulk Import Tab Component
function ImportTab({ onSuccess }) {
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a CSV file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`/api/v1/bulk-import/csv?tenant_id=${tenantId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setResults(response.data);
      toast.success(`Import completed! ${response.data.success_count} items imported successfully.`);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = ['SKU', 'Product Name', 'Category', 'Subcategory', 'Capacity', 'UoM', 'Material', 'Type', 'Hooks', 'Finish', 'Cost', 'Price', 'Barcode', 'Brand'];
    const sampleData = [
      'FBUCKET-S-SS,Stainless Steel Flat Bucket Small,Bowls & Feeders,Flat Bucket,1,QT,Stainless Steel,Food Bowl,FALSE,Brushed,12.50,24.99,123456789012,PetYupp',
      'RBUCKET-M-PL,Plastic Round Bucket Medium,Bowls & Feeders,Round Bucket,2,QT,Plastic,Water Bowl,FALSE,Matte,8.75,17.99,123456789013,PetYupp',
      'FBUCKET-L-SS-H,Flat Bucket with Hooks Large,Bowls & Feeders,Flat Bucket with Hooks,3,QT,Stainless Steel,Food Bowl,TRUE,Glossy,18.25,34.99,123456789014,PetYupp'
    ];
    
    const csvContent = [headers.join(','), ...sampleData].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'petyupp_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Bulk Import</h2>
        <p className="text-slate-600">Import multiple products and variants from CSV file</p>
      </div>

      {/* Template Download */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            CSV Template
          </CardTitle>
          <CardDescription>
            Download the CSV template with correct column structure
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Required columns: SKU, Product Name, Category, Subcategory, Capacity, UoM, Material, Type, Hooks, Finish, Cost, Price
            </p>
            <Button onClick={downloadTemplate} variant="outline" className="gap-2">
              <Download className="w-4 h-4" />
              Download Template
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* File Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Upload CSV File
          </CardTitle>
          <CardDescription>
            Drag and drop your CSV file or click to select
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-300'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-600 mb-2">
              {file ? file.name : 'Drop your CSV file here'}
            </p>
            <p className="text-sm text-slate-500 mb-4">
              or click to select a file
            </p>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <Button variant="outline" className="cursor-pointer">
                Select File
              </Button>
            </label>
          </div>
          
          {file && (
            <div className="mt-4 flex justify-between items-center">
              <span className="text-sm text-slate-600">Selected: {file.name}</span>
              <Button onClick={handleUpload} disabled={uploading} className="gap-2">
                <Upload className="w-4 h-4" />
                {uploading ? 'Importing...' : 'Import CSV'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import Results */}
      {results && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Import Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-green-600 font-medium">Successful</p>
                  <p className="text-2xl font-bold text-green-800">{results.success_count}</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-red-600 font-medium">Errors</p>
                  <p className="text-2xl font-bold text-red-800">{results.error_count}</p>
                </div>
              </div>
              
              {results.errors && results.errors.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Errors Details:</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {results.errors.map((error, index) => (
                      <div key={index} className="p-2 bg-red-50 border border-red-200 rounded text-sm">
                        <p><strong>Row {error.row}:</strong> {error.error}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default App;